/* ============================================================
   Polish Gallery — live layer (no library needed)
   1. Hero slideshow: photos crossfade with a slow zoom, the copy
      rises in; on phones numbered tabs carry a progress line
      (hidden on desktop, user 1 Oct). Pauses on hover, off screen
      and in a hidden tab; swipe on touch.
   2. Product cards play more photos of the same design: on hover
      (mouse) or while the card is in view (touch). Size charts and
      other mostly-white, colourless images are skipped (checked on a
      48 px copy, remembered for the visit).
   3. Search suggestions: popular searches and Christmas picks on
      focus, then live results as you type, Christmas items first.
   Visitors who prefer reduced motion get no autoplay; everything
   stays usable by click.
   ============================================================ */
(function () {
  'use strict';

  // Motion for every visitor, also when the OS asks for less (owner, 1 Oct 2026).
  const reduce = false;
  const fineHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------- 1. Hero slideshow ---------- */
  function heroSlides() {
    const hero = document.querySelector('[data-pg-hero]');
    if (!hero) return;
    const frames = Array.from(hero.querySelectorAll('.pg-hero__frame'));
    const copies = Array.from(hero.querySelectorAll('.pg-hero__copy'));
    const tabs = Array.from(hero.querySelectorAll('.pg-hero__tab'));
    const n = frames.length;
    if (n < 2) return;
    let current = 0;
    let elapsed = 0;
    // Time per slide from the section (--pg-hero-int, e.g. 7s); the first slide waits 1.5 s more for the page intro.
    const per = (parseFloat(getComputedStyle(hero).getPropertyValue('--pg-hero-int')) || 7) * 1000;
    const holds = new Set();

    const warm = (i) => {
      const img = frames[i] && frames[i].querySelector('img');
      if (img && img.loading === 'lazy') img.loading = 'eager';
    };

    const show = (next) => {
      next = (next + n) % n;
      if (next === current) return;
      hero.classList.remove('is-first');
      frames[current].classList.remove('is-active');
      frames[current].classList.add('is-leaving');
      const leaving = frames[current];
      setTimeout(() => leaving.classList.remove('is-leaving'), 1600);
      current = next;
      frames.forEach((f, i) => f.classList.toggle('is-active', i === current));
      copies.forEach((c, i) => {
        const on = i === current;
        c.classList.toggle('is-active', on);
        c.toggleAttribute('inert', !on);
        c.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      tabs.forEach((t, i) => {
        t.classList.toggle('is-active', i === current);
        t.setAttribute('aria-selected', i === current ? 'true' : 'false');
      });
      warm((current + 1) % n);
      elapsed = 0;
    };

    // Timer (counts only while nothing holds the slideshow). The tab progress
    // lines run on the same time and pause with it; tabs show on phones only.
    tabs.forEach((tab, i) => tab.addEventListener('click', () => show(i)));
    let first = true;
    setInterval(() => {
      if (holds.size) return;
      elapsed += 200;
      if (elapsed >= per + (first ? 1500 : 0)) { first = false; show(current + 1); }
    }, 200);

    const hold = (why, on) => {
      if (on) holds.add(why); else holds.delete(why);
      hero.classList.toggle('is-paused', holds.size > 0);
    };
    if (reduce) hold('reduce', true);
    const content = hero.querySelector('.pg-hero__content');
    if (content && fineHover) {
      content.addEventListener('pointerenter', () => hold('hover', true));
      content.addEventListener('pointerleave', () => hold('hover', false));
    }
    hero.addEventListener('focusin', () => hold('focus', true));
    hero.addEventListener('focusout', () => hold('focus', false));
    document.addEventListener('visibilitychange', () => hold('hidden', document.hidden));
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        entries.forEach((e) => hold('offscreen', !e.isIntersecting));
      }, { threshold: 0.2 }).observe(hero);
    }

    // Swipe
    let x0 = null;
    let y0 = null;
    hero.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
    hero.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      const dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) show(current + (dx < 0 ? 1 : -1));
      x0 = null;
    }, { passive: true });

    warm(1);
    hero.classList.add('is-ready', 'is-first');
  }

  /* ---------- 2. Product card photo slideshows ---------- */
  const verdicts = (() => {
    try { return JSON.parse(sessionStorage.getItem('pgAltOk') || '{}'); } catch (e) { return {}; }
  })();
  const pending = new Map();
  let saveTimer = null;
  const saveVerdicts = () => {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      try { sessionStorage.setItem('pgAltOk', JSON.stringify(verdicts)); } catch (e) { /* storage off */ }
    }, 400);
  };
  const fileKey = (u) => u.split('?')[0].split('/').pop();

  // true = a photo worth showing; false = a size chart / mostly white page.
  function worthShowing(url) {
    const key = fileKey(url);
    if (key in verdicts) return Promise.resolve(verdicts[key]);
    if (pending.has(key)) return pending.get(key);
    const small = /[?&]width=\d+/.test(url) ? url.replace(/([?&]width=)\d+/, '$148') : url + (url.includes('?') ? '&' : '?') + 'width=48';
    const job = fetch(small, { mode: 'cors' })
      .then((r) => r.blob())
      .then((b) => createImageBitmap(b))
      .then((bmp) => {
        const c = document.createElement('canvas');
        c.width = 48;
        c.height = 48;
        const ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(bmp, 0, 0, 48, 48);
        const d = ctx.getImageData(0, 0, 48, 48).data;
        let white = 0;
        let colour = 0;
        for (let i = 0; i < d.length; i += 4) {
          const r = d[i], g = d[i + 1], b = d[i + 2];
          if (r > 232 && g > 232 && b > 232) white++;
          colour += Math.max(r, g, b) - Math.min(r, g, b);
        }
        const px = d.length / 4;
        const ok = !(white / px >= 0.55 && colour / px <= 6);
        verdicts[key] = ok;
        saveVerdicts();
        return ok;
      })
      .catch(() => true);
    pending.set(key, job);
    return job;
  }

  function cardSlides() {
    const medias = document.querySelectorAll('.pg-card__media[data-pg-alts]');
    if (!medias.length) return;

    const setup = (media) => {
      if (media._pg) return media._pg;
      const urls = media.dataset.pgAlts.split(/\s+/).filter(Boolean);
      media._pg = Promise.all(urls.map((u) => worthShowing(u).then((ok) => (ok ? u : null)))).then((list) => {
        const keep = list.filter(Boolean).slice(0, 5);
        const card = media.closest('.pg-card');
        if (!keep.length || !card) return null;
        const layer = document.createElement('div');
        layer.className = 'pg-card__alts';
        layer.setAttribute('aria-hidden', 'true');
        const imgs = keep.map((u) => {
          const img = document.createElement('img');
          img.className = 'pg-card__alt';
          img.alt = '';
          img.decoding = 'async';
          img.src = u;
          layer.appendChild(img);
          return img;
        });
        const dots = document.createElement('div');
        dots.className = 'pg-card__dots';
        dots.setAttribute('aria-hidden', 'true');
        const marks = [0].concat(keep.map((_, i) => i + 1)).map(() => dots.appendChild(document.createElement('i')));
        // Inside the photo frame, like the main photo: never under the name and price (user 1 Oct).
        media.appendChild(layer);
        card.appendChild(dots);
        return { card, imgs, marks, index: 0, timer: null };
      });
      return media._pg;
    };

    const paint = (st) => {
      st.card.classList.toggle('is-alt', st.index > 0);
      st.imgs.forEach((img, i) => img.classList.toggle('is-on', i + 1 === st.index));
      st.marks.forEach((m, i) => m.classList.toggle('is-on', i === st.index));
    };

    const step = (st) => {
      const next = (st.index + 1) % (st.imgs.length + 1);
      const img = st.imgs[next - 1];
      // Wait for the next photo rather than flashing an empty frame.
      if (img && !(img.complete && img.naturalWidth)) return;
      st.index = next;
      paint(st);
    };

    const start = (media, every, firstDelay) => {
      setup(media).then((st) => {
        if (!st || st.timer || !media._want) return;
        st.card.classList.add('is-playing');
        st.timer = setTimeout(function tick() {
          step(st);
          st.timer = setTimeout(tick, every);
        }, firstDelay);
      });
    };

    const stop = (media) => {
      if (!media._pg) return;
      media._pg.then((st) => {
        if (!st) return;
        clearTimeout(st.timer);
        st.timer = null;
        st.index = 0;
        paint(st);
        st.card.classList.remove('is-playing');
      });
    };

    if (fineHover) {
      medias.forEach((media) => {
        const card = media.closest('.pg-card');
        if (!card) return;
        card.addEventListener('pointerenter', () => { media._want = true; start(media, 1300, 260); });
        card.addEventListener('pointerleave', () => { media._want = false; stop(media); });
        // Get the checks done before the pointer arrives.
        card.addEventListener('pointerover', () => setup(media), { once: true });
      });
    } else if (!reduce && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const media = e.target;
          media._want = e.isIntersecting;
          if (e.isIntersecting) start(media, 2000, 700 + Math.random() * 900);
          else stop(media);
        });
      }, { threshold: 0.6 });
      medias.forEach((m) => io.observe(m));
    }
  }

  /* ---------- 3. Search suggestions ---------- */
  function searchSuggest() {
    const panel = document.querySelector('[data-pg-suggest]');
    if (!panel) return;
    const form = panel.closest('form');
    const input = form && form.querySelector('input[name="q"]');
    const live = panel.querySelector('[data-pg-suggest-live]');
    if (!input || !live) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const festive = /christmas|ornament|xmas|topper|nativity|advent|wigilia|op[lł]at|winter|santa|angel|bauble|glass ball/i;
    let seq = 0;
    let debounce = null;

    const open = () => { panel.hidden = false; form.classList.add('is-suggesting'); };
    const close = () => { panel.hidden = true; form.classList.remove('is-suggesting'); };
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const isFestive = (p) => festive.test([p.title, p.type, p.product_type, (p.tags || []).join(' ')].join(' '));
    const money = (v) => {
      const n = parseFloat(v);
      if (isNaN(n)) return '';
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' }).format(n); } catch (e) { return '$' + n.toFixed(2); }
    };

    const render = (q, res) => {
      const products = (res.products || []).map((p, i) => ({ p, i, f: isFestive(p) ? 0 : 1 }))
        .sort((a, b) => a.f - b.f || a.i - b.i).map((x) => x.p).slice(0, 6);
      const queries = (res.queries || []).slice().sort((a, b) => (festive.test(b.text) ? 1 : 0) - (festive.test(a.text) ? 1 : 0)).slice(0, 4);
      const cols = (res.collections || []).slice(0, 3);
      let html = '';
      if (queries.length) {
        html += '<div class="pg-suggest__chips">' + queries.map((s) =>
          '<a class="pg-suggest__chip" href="' + esc(s.url) + '">' + (s.styled_text || esc(s.text)) + '</a>').join('') + '</div>';
      }
      if (cols.length) {
        html += '<p class="pg-suggest__label">Collections</p><div class="pg-suggest__chips">' + cols.map((c) =>
          '<a class="pg-suggest__chip pg-suggest__chip--col" href="' + esc(c.url) + '">' + esc(c.title) + '</a>').join('') + '</div>';
      }
      if (products.length) {
        html += '<p class="pg-suggest__label">Products</p><div class="pg-suggest__picks">' + products.map((p) => {
          const img = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
          const src = img ? img + (img.includes('?') ? '&' : '?') + 'width=160' : '';
          return '<a class="pg-suggest__item" href="' + esc(p.url) + '">' +
            (src ? '<img class="pg-suggest__thumb" src="' + esc(src) + '" alt="">' : '') +
            '<span class="pg-suggest__name">' + esc(String(p.title).split(',')[0]) + '</span>' +
            '<span class="pg-suggest__price">' + esc(money(p.price)) + '</span></a>';
        }).join('') + '</div>';
      }
      html += '<a class="pg-suggest__all" href="' + esc(root) + 'search?type=product&q=' + encodeURIComponent(q) + '">See all results for “' + esc(q) + '”</a>';
      live.innerHTML = html;
      panel.classList.add('is-live');
    };

    const lookup = () => {
      const q = input.value.trim();
      if (!q) { panel.classList.remove('is-live'); live.innerHTML = ''; return; }
      const mine = ++seq;
      const url = root + 'search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=query,product,collection&resources[limit]=10&resources[options][unavailable_products]=last';
      fetch(url).then((r) => r.json()).then((j) => {
        if (mine !== seq) return;
        render(q, (j.resources && j.resources.results) || {});
      }).catch(() => {});
    };

    input.addEventListener('focus', open);
    input.addEventListener('input', () => { open(); clearTimeout(debounce); debounce = setTimeout(lookup, 180); });
    input.addEventListener('keydown', (e) => { if (e.key === 'Escape') { close(); input.blur(); } });
    document.addEventListener('pointerdown', (e) => { if (!form.contains(e.target)) close(); });
    form.addEventListener('focusout', (e) => { if (!form.contains(e.relatedTarget)) setTimeout(() => { if (!form.contains(document.activeElement)) close(); }, 120); });
    if (input.value.trim()) lookup();
  }

  function init() {
    heroSlides();
    cardSlides();
    searchSuggest();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
